import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FormField, FormSelect, FormTextarea, SectionHeader } from '../../form';
import { Field, Input, Label } from '@headlessui/react';

const WhitelabelGateway: React.FC<any> = ({ formik }) => {
    const [otherChecked, setOtherChecked] = useState(formik.values.wlGatewayFeatures?.includes('Other') || false);

    const handleGatewayFeatureChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const { value, checked } = event.target;
        let updatedFeatures = [...formik.values.wlGatewayFeatures];

        if (checked) {
            updatedFeatures.push(value);
        } else {
            updatedFeatures = updatedFeatures.filter((feature) => feature !== value);
        }

        formik.setFieldValue('wlGatewayFeatures', updatedFeatures);

        if (value === 'Other') {
            setOtherChecked(checked);
        }
    };

    const { t } = useTranslation();

    return (
        <SectionHeader
            title={t('prechecks.whitelabelGateway')}
            icon={
                <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 3v2m6-2v2M9 19v2m6-2v2M5 9H3m2 6H3m18-6h-2m2 6h-2M7 19h10a2 2 0 002-2V7a2 2 0 00-2-2H7a2 2 0 00-2 2v10a2 2 0 002 2zM9 9h6v6H9V9z" />
                </svg>
            }
            iconBgColor="bg-indigo-100"
            iconTextColor="text-indigo-600"
            titleColor="text-indigo-600"
        >
            <FormField
                id="wlExpMonthVol"
                name="wlExpMonthVol"
                label={t('prechecks.expectedMonthlyProcessingVolumeEURUSD')}
                type="text"
                value={formik.values.wlExpMonthVol}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.errors.wlExpMonthVol}
                touched={formik.touched.wlExpMonthVol}
            />

            <FormField
                id="wlExpTxCount"
                name="wlExpTxCount"
                label={t('prechecks.expectedMonthlyTransactionCount')}
                type="text"
                value={formik.values.wlExpTxCount}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.errors.wlExpTxCount}
                touched={formik.touched.wlExpTxCount}
            />

            <FormField
                id="wlAvgTxVal"
                name="wlAvgTxVal"
                label={t('prechecks.averageTransactionValue')}
                type="text"
                value={formik.values.wlAvgTxVal}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.errors.wlAvgTxVal}
                touched={formik.touched.wlAvgTxVal}
            />

            <FormSelect
                id="wlLicense"
                name="wlLicense"
                label={t('prechecks.doYouHoldALicense')}
                value={formik.values.wlLicense}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                options={[
                    { value: 'no', label: t('common.no') },
                    { value: 'yes', label: t('common.yes') },
                ]}
                error={formik.errors.wlLicense}
                touched={formik.touched.wlLicense}
            />

            <FormField
                id="wlLicJur"
                name="wlLicJur"
                label={t('prechecks.licenseJurisdiction')}
                type="text"
                value={formik.values.wlLicJur}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
            />

            {/* Custom checkbox field for wlGatewayFeatures with state management */}
            <Field>
                <Label className="text-base font-semibold text-slate-700 mb-4 block">
                    {t('prechecks.neededGatewayFeatures')}
                </Label>
                <div className="bg-white rounded-lg p-4 border border-slate-200">
                    {[
                        { value: 'Risk management', key: 'riskManagement' },
                        { value: 'Load balancing', key: 'loadBalancing' },
                        { value: 'Chargeback & fraud alert management', key: 'chargebackFraudAlertManagement' },
                        { value: 'MID configuration', key: 'midConfiguration' },
                        { value: 'BIN & IP block', key: 'binIpBlock' },
                        { value: 'Other', key: 'other' },
                    ].map((option, index) => (
                        <Field key={index} className="flex items-center mb-2">
                            <Input
                                type="checkbox"
                                name="wlGatewayFeatures"
                                value={option.value}
                                onChange={handleGatewayFeatureChange}
                                className="w-5 h-5 text-blue-600 bg-white border-slate-300 rounded focus:ring-2 focus:ring-blue-500 transition duration-150 ease-in-out cursor-pointer"
                            />
                            <Label className="text-base font-medium text-slate-700 ml-3 cursor-pointer">
                                {option.key === 'other' ? t('common.other') : t(`prechecks.${option.key}`)}
                            </Label>
                        </Field>
                    ))}
                </div>
                {formik.touched.wlGatewayFeatures && formik.errors.wlGatewayFeatures ? (
                    <div className="text-red-700 text-sm mt-2 bg-red-50 border border-red-200 p-2 rounded">{formik.errors.wlGatewayFeatures}</div>
                ) : null}
            </Field>

            {otherChecked && (
                <FormTextarea
                    id="wlOtherGateway"
                    name="wlOtherGateway"
                    label={t('prechecks.specifyOtherGatewayFeatures')}
                    value={formik.values.wlOtherGateway}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    error={formik.errors.wlOtherGateway}
                    touched={formik.touched.wlOtherGateway}
                    rows={4}
                />
            )}

            <FormField
                id="wlTargetMarkets"
                name="wlTargetMarkets"
                label={t('prechecks.targetMarkets')}
                type="text"
                value={formik.values.wlTargetMarkets}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.errors.wlTargetMarkets}
                touched={formik.touched.wlTargetMarkets}
            />

            <FormField
                id="wlBankAcquirers"
                name="wlBankAcquirers"
                label={t('prechecks.neededBankAcquirers')}
                type="text"
                value={formik.values.wlBankAcquirers}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.errors.wlBankAcquirers}
                touched={formik.touched.wlBankAcquirers}
            />

            <FormField
                id="wlAltBilling"
                name="wlAltBilling"
                label={t('prechecks.neededAlternativeBillingSolutions')}
                type="text"
                value={formik.values.wlAltBilling}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.errors.wlAltBilling}
                touched={formik.touched.wlAltBilling}
            />

            <FormTextarea
                id="wlOtherRemarks"
                name="wlOtherRemarks"
                label={t('prechecks.otherRemarks')}
                value={formik.values.wlOtherRemarks}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                rows={4}
            />
        </SectionHeader>
    );
};

export default WhitelabelGateway;
